import { Package } from "lucide-react"
import { Hits, Stats, useInstantSearch } from "react-instantsearch"
import { Product } from "../../types/search"
import { cn } from "../../utils/cn"
import { ProductCard } from "./ProductCard"

interface CollectionResultsProps {
	collectionName: string
	onProductClick?: (product: Product) => void
	className?: string
	hasSemanticSearch?: boolean
	onFindSimilar?: (product: Product) => void
}

/**
 * CollectionResults displays search results for a single collection
 * Used in federated multi-collection search to show results separately per collection
 */
export const CollectionResults = ({
	collectionName,
	onProductClick,
	className,
	hasSemanticSearch,
	onFindSimilar,
}: CollectionResultsProps) => {
	const { results } = useInstantSearch()

	// Don't render if no results for this collection
	if (!results || results.nbHits === 0) {
		return null
	}

	return (
		<div className={cn("mb-8", className)}>
			{/* Collection Header */}
			<div className="flex items-center justify-between mb-4 pb-2 border-b border-border">
				<div className="flex items-center gap-2">
					<Package className="w-5 h-5 text-primary" />
					<h3 className="text-lg font-semibold capitalize">{collectionName.replace(/_/g, " ")}</h3>
				</div>
				<div className="text-sm text-muted-foreground">
					<Stats
						classNames={{
							root: "text-sm text-muted-foreground",
						}}
						translations={{
							rootElementText({ nbHits }) {
								return `${nbHits.toLocaleString()} result${nbHits !== 1 ? "s" : ""}`
							},
						}}
					/>
				</div>
			</div>

			{/* Results Grid */}
			<Hits
				classNames={{
					root: "w-full",
					list: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4",
					item: "list-none",
				}}
				hitComponent={({ hit }) => (
					<ProductCard
						hasSemanticSearch={hasSemanticSearch}
						onClick={onProductClick}
						onFindSimilar={onFindSimilar}
						product={hit as any}
					/>
				)}
			/>
		</div>
	)
}
